import React, { useEffect, useState } from 'react';
import { Button, FormGroup, HTMLSelect, InputGroup } from '@blueprintjs/core';
import { IDENTITY_TRANSFORM, isIdentityTransform, matchRange, SignalTransform, ValueRange } from './signalTransform';

interface SignalTransformEditorProps {
  name: string;
  transform: SignalTransform;
  onChange: (transform: SignalTransform) => void;
  // Value ranges of all signals, to fit this one onto another.
  ranges: Map<string, ValueRange>;
  // The input being edited is drawn as is, so its points can be dragged.
  isEditing?: boolean;
}

type Field = keyof SignalTransform;

const FIELDS: { key: Field; label: string; helper: string }[] = [
  { key: 'timeShift', label: 'Time shift [s]', helper: 'Positive moves the signal later.' },
  { key: 'scale', label: 'Scale', helper: 'Values are multiplied by this.' },
  { key: 'offset', label: 'Offset', helper: 'Added after scaling.' },
];

const toDrafts = (transform: SignalTransform): Record<Field, string> => ({
  timeShift: `${transform.timeShift}`,
  scale: `${transform.scale}`,
  offset: `${transform.offset}`,
});

// Edits how one signal is drawn. Fields keep what is typed, so "-" or "0."
// can be typed on the way to a number; only valid numbers are applied.
export const SignalTransformEditor: React.FC<SignalTransformEditorProps> = ({ name, transform, onChange, ranges, isEditing = false }) => {
  const [drafts, setDrafts] = useState(() => toDrafts(transform));

  // Follow changes made elsewhere (reset, fit to another signal).
  useEffect(() => {
    setDrafts((current) => (Object.keys(current) as Field[]).every((key) => Number(current[key]) === transform[key])
      ? current
      : toDrafts(transform));
  }, [transform]);

  const handleFieldChange = (key: Field, text: string) => {
    setDrafts((current) => ({ ...current, [key]: text }));
    const value = Number(text);

    if (text.trim() !== '' && Number.isFinite(value)) {
      onChange({ ...transform, [key]: value });
    }
  };

  const ownRange = ranges.get(name);
  const targets = Array.from(ranges.keys()).filter((other) => other !== name);

  const fitTo = (target: string) => {
    const targetRange = ranges.get(target);

    if (ownRange && targetRange) {
      onChange({ ...transform, ...matchRange(ownRange, targetRange) });
    }
  };

  return (
    <div className="cm-transform-editor">
      <h3 className="cm-transform-title">{name}</h3>
      {isEditing && (
        <p className="cm-transform-note">Drawn unchanged while you edit its points.</p>
      )}
      {FIELDS.map(({ key, label, helper }) => (
        <FormGroup key={key} label={label} labelFor={`transform-${key}`} helperText={helper}>
          <InputGroup
            id={`transform-${key}`}
            small
            inputMode="decimal"
            value={drafts[key]}
            intent={drafts[key].trim() === '' || !Number.isFinite(Number(drafts[key])) ? 'danger' : 'none'}
            onChange={(event) => handleFieldChange(key, event.target.value)}
          />
        </FormGroup>
      ))}
      {ownRange && targets.length > 0 && (
        <FormGroup label="Fit to the range of" labelFor="transform-fit" helperText="Sets scale and offset so this signal spans the same values.">
          <HTMLSelect
            id="transform-fit"
            fill
            value=""
            onChange={(event) => fitTo(event.target.value)}
            options={[{ label: 'Choose a signal…', value: '' }, ...targets.map((target) => ({ label: target, value: target }))]}
          />
        </FormGroup>
      )}
      <Button small disabled={isIdentityTransform(transform)} onClick={() => onChange(IDENTITY_TRANSFORM)}>
        Reset
      </Button>
    </div>
  );
};
